import { Component, inject, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  PreviewPlanActivationResponse,
  SubscriptionsService
} from '../../../services/subscriptions'
import { PaymentsService, SubscriptionInitiationResponse } from '../../../services/payments.service'
import { ActivatedRoute, Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  switchMap,
  take,
  tap
} from 'rxjs'
import { eps } from '../../../services/url.service'
import { AuthState, selectSubscription, selectUser } from '../../../store/auth/auth.state'
import { CommonModule } from '@angular/common'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { RazorpayService } from '../../../services/razorpay.service'

@Component({
  selector: 'app-activate-plan',
  imports: [CommonModule, CapitalizePipe],
  templateUrl: './activate-plan.component.html',
  styleUrl: './activate-plan.component.scss'
})
export class ActivatePlanComponent implements OnInit {
  private store = inject(Store)
  private subscriptionsService = inject(SubscriptionsService)
  private razorpayService = inject(RazorpayService)
  private paymentsService = inject(PaymentsService)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private toastr = inject(ToastrService)

  selectedPlanId!: string
  currentSubscriptionId!: string
  businessId!: string
  isProcessingPayment = false
  planActivationSummary$!: Observable<PreviewPlanActivationResponse>
  user: AuthState['user'] | null = null
  private userInputLicences$ = new BehaviorSubject<number | null>(null)

  ngOnInit(): void {
    const selectedPlanId = this.route.snapshot.paramMap.get('id')
    if (!selectedPlanId) this.router.navigate([eps.hrm.upgradePlanPath])
    else this.selectedPlanId = selectedPlanId

    const initialData$ = combineLatest([
      this.store.select(selectSubscription),
      this.store.select(selectUser).pipe(map(user => user?.businessId))
    ]).pipe(
      filter(([sub, busId]) => !!sub && !!busId),
      take(1),
      tap(([sub, busId]) => {
        this.currentSubscriptionId = sub?.id!
        this.businessId = busId!
      })
    )

    // The main reactive stream. It re-triggers whenever the license count changes.
    this.planActivationSummary$ = combineLatest([
      initialData$,
      this.userInputLicences$.pipe(debounceTime(300), distinctUntilChanged())
    ]).pipe(
      switchMap(([[sub, busId], licencesCount]) => {
        return this.subscriptionsService.previewPlanActivation(
          sub?.id!,
          this.selectedPlanId,
          busId!,
          licencesCount ?? 0
        )
      }),
      tap(summary => {
        if (this.userInputLicences$.value === null) {
          this.userInputLicences$.next(summary.plan.requestedLicences)
        }
      })
    )

    this.store
      .select(selectUser)
      .pipe(
        take(1),
        tap(user => {
          this.user = user
        })
      )
      .subscribe()
  }

  onLicenceChange(newCount: number, minLicences: number): void {
    const validatedCount = Math.max(minLicences, Number(newCount) || minLicences)
    this.userInputLicences$.next(validatedCount)
  }

  //getPaymentLink() {
  //  this.planActivationSummary$.pipe(take(1)).subscribe(summary => {
  //    const finalLicencesCount = summary.plan.requestedLicences
  //    this.paymentsService
  //      .getActivationPaymentLink(
  //        this.currentSubscriptionId,
  //        this.selectedPlanId,
  //        finalLicencesCount
  //      )
  //      .pipe(take(1))
  //      .subscribe(r => {
  //        if (r.message && !r.paymentNeeded && !r.redirectUrl) {
  //          this.toastr.success(r.message)
  //          this.router.navigate([eps.planAndBilling.planPath])
  //        }
  //        if (r.paymentNeeded && r.redirectUrl) window.location.href = r.redirectUrl
  //      })
  //  })
  //}

  activateSubscription(): void {
    if (this.isProcessingPayment) return
    this.isProcessingPayment = true

    this.planActivationSummary$.pipe(take(1)).subscribe(summary => {
      const finalLicencesCount = summary.plan.requestedLicences

      this.paymentsService
        .initiateSubscription(this.currentSubscriptionId, this.selectedPlanId, finalLicencesCount)
        .pipe(take(1))
        .subscribe({
          next: response => {
            this.showRazorpayCheckout(response, summary)
          },
          error: err => {
            this.isProcessingPayment = false
            this.toastr.error(err.error?.message || 'Failed to initiate payment. Please try again.')
          }
        })
    })
  }

  //kripesh
  // private showRazorpayCheckout(
  //   initiationResponse: SubscriptionInitiationResponse,
  //   summary: PreviewPlanActivationResponse
  // ): void {
  //   this.razorpayService
  //     .load()
  //     .then(() => {
  //       const options = {
  //         key: initiationResponse.key,
  //         // Use order_id for monthly (prorated) or subscription_id for yearly
  //         ...(initiationResponse.type === 'order' && {
  //           order_id: initiationResponse.orderId,
  //           amount: initiationResponse.amount,
  //           currency: initiationResponse.currency
  //         }),
  //         ...(initiationResponse.type === 'subscription' && {
  //           subscription_id: initiationResponse.subscriptionId
  //         }),

  //         name: 'AVR HRMS',
  //         description: `Activating ${summary.plan.name}`,

  //         handler: (response: any) => {
  //           // This function is called on successful payment
  //           console.log('Razorpay success response', response)
  //           this.toastr.success('Payment successful! Your plan is being activated.')
  //           // Navigate the user to a success page.
  //           this.router.navigate([eps.planAndBilling.planPath])
  //         },

  //         prefill: {
  //           // You can prefill user details from your store if available
  //           name: `${this.user?.firstName ?? ''} ${this.user?.lastName ?? ''}`,
  //           email: this.user?.email ?? '',
  //           contact: this.user?.mobile ?? ''
  //         },

  //         notes: {
  //           plan: summary.plan.name,
  //           licences: summary.plan.requestedLicences
  //         },

  //         modal: {
  //           ondismiss: () => {
  //             this.isProcessingPayment = false
  //             this.toastr.info('Payment was not completed.')
  //           }
  //         },

  //         theme: {
  //           color: '#3399cc' // Your brand color
  //         }
  //       }

  //       // Create and open the Razorpay Checkout
  //       const rzp = new window.Razorpay(options)
  //       rzp.open()
  //     })
  //     .catch(error => {
  //       console.error('Failed to load Razorpay script', error)
  //       this.toastr.error(
  //         'Could not connect to the payment gateway. Please check your connection and try again.'
  //       )
  //       this.isProcessingPayment = false
  //     })
  // }

  //gps 25 feb 2026 for monthly subscription active
  private showRazorpayCheckout(
    initiationResponse: SubscriptionInitiationResponse,
    summary: PreviewPlanActivationResponse
  ): void {
    this.razorpayService
      .load()
      .then(() => {
        const options = {
          key: initiationResponse.key,
          // Use order_id for monthly (prorated) or subscription_id for yearly
          ...(initiationResponse.type === 'order' && {
            order_id: initiationResponse.orderId,
            amount: initiationResponse.amount,
            currency: initiationResponse.currency
          }),
          ...(initiationResponse.type === 'subscription' && {
            subscription_id: initiationResponse.subscriptionId
          }),

          name: 'AVR HRMS',
          description: `Activating ${summary.plan.name}`,

          handler: (response: any) => {
            // This function is called on successful payment
            console.log('Razorpay success response', response)
            console.log('initiationResponse', initiationResponse)
            console.log('initiationResponse subscriptionId', response.razorpay_payment_id)
            if (initiationResponse.type === 'order' && response.razorpay_payment_id) {
              // STEP 1 COMPLETE → NOW AUTHENTICATE MANDATE

              this.paymentsService
                .authenticateMandate(response.razorpay_payment_id)
                .subscribe(res => {
                  const mandateOptions = {
                    key: res.key,
                    subscription_id: res.subscriptionId,
                    name: 'AVR HRMS',
                    description: 'Enable Auto Debit'
                  }

                  const mandateRzp = new window.Razorpay(mandateOptions)
                  mandateRzp.open()
                })
            } else {
              // Yearly flow
              this.router.navigate([eps.planAndBilling.planPath])
            }
            this.toastr.success('Payment successful! Your plan is being activated.')
            // Navigate the user to a success page.
            this.router.navigate([eps.planAndBilling.planPath])
          },

          prefill: {
            // You can prefill user details from your store if available
            name: `${this.user?.firstName ?? ''} ${this.user?.lastName ?? ''}`,
            email: this.user?.email ?? '',
            contact: this.user?.mobile ?? ''
          },

          notes: {
            plan: summary.plan.name,
            licences: summary.plan.requestedLicences
          },

          modal: {
            ondismiss: () => {
              this.isProcessingPayment = false
              this.toastr.info('Payment was not completed.')
            }
          },

          theme: {
            color: '#3399cc' // Your brand color
          }
        }

        // Create and open the Razorpay Checkout
        const rzp = new window.Razorpay(options)
        rzp.open()
      })
      .catch(error => {
        console.error('Failed to load Razorpay script', error)
        this.toastr.error(
          'Could not connect to the payment gateway. Please check your connection and try again.'
        )
        this.isProcessingPayment = false
      })
  }
}
